import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    fetchProductAvailablePropertiesBeforeCheckout,
    priceCalculatorBeforeCheckout,
    fetchProjectDetail,
    fetchProductDetail,
    submitOrder,
    toggleShippingAddressStatus,
    addShippingAddress,
    fetchPaymentDetailAfterPayment
} from '../../redux/slices/client/clientOrderAndPaymentSlice';
import { debounce } from 'lodash';
import CheckoutOrderCouponsWidget from '../../components/common/widgets/CheckoutOrderCouponsWidget';
import { thunkFetchExpiringCouponsForClient } from '../../redux/slices/couponSlice';

import CheckoutExpiringAlert from '../../components/common/widgets/CheckoutExpiringAlert';
const Breadcrumbs = ({ pathToBackwardTraverse }) => {
    return (
        <div className="bg-[#0B0C52] w-full px-4  text-sm text-white">
            <nav>
                {pathToBackwardTraverse.map((item, index) => (
                    <React.Fragment key={index}>
                        <Link to={item.path} className="hover:underline">
                            {item.name}
                        </Link>
                        {index < pathToBackwardTraverse.length - 1 && (
                            <span className="mx-2">/</span>
                        )}
                    </React.Fragment>
                ))}
                <span className="mx-2">/</span>
                <span className="font-medium text-[#FFD700]">Checkout</span>
            </nav>
        </div>
    );
};

const SuccessfulPaymentConfirmation = ({ orderData, navigate }) => {
    // Default data in case orderData is not provided
    const order = orderData || {
        id: 7,
        transaction_id: "-",
        created_at: "MM DD, YYYY",
        order_detail: {
            order_uid: "ZZZZ-ZZZZ-ZZZZ-ZZZZ-ZZZZZZZZZZZ",
            customer_full_name: "XXXXX XXXX",
            status: "Order Placed",
            payment_status: "-",
            shipping_address: {
                first_name: "-",
                last_name: "-",
                address_line_1: "-",
                address_line_2: "-",
                country: "-",
                state_or_province: "-",
                city: "-",
                zip_code: "-"
            }
        },
        method: "-",
        amount: "N/A",
        status: "-"
    };
    const step = 2;

    // Format the amount as currency
    const formattedAmount = `$${parseFloat(order.amount).toFixed(2)}`;

    return (
        <div>

            <div className="flex items-center justify-center py-8 bg-gray-50">
                <div className="text-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center mx-auto ${step === 1 ? 'bg-blue-600 text-white' : 'bg-gray-400 text-white opacity-50'}`}>
                        1
                    </div>
                    <p className={`text-sm mt-2 font-semibold ${step === 1 ? 'text-black' : 'opacity-50'}`}>
                        Check Out
                    </p>
                </div>
                <div className="flex-2 w-16 border-t-2 border-gray-300"></div>
                <div className="text-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center mx-auto ${step === 2 ? 'bg-blue-600 text-white' : 'bg-gray-400 text-white opacity-50'}`}>
                        2
                    </div>
                    <p className={`text-sm mt-2 font-semibold ${step === 2 ? 'text-black' : 'opacity-50'}`}>
                        Confirmation
                    </p>
                </div>
            </div>

            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">


                <div className="max-w-md w-full bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
                    {/* Header with blue accent */}
                    <div className="bg-[#0B0C52] p-6 text-center">
                        <div className="flex justify-center mb-4">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-[#FFD100]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <h1 className="text-2xl font-bold text-white mb-2">Payment Successful!</h1>
                        <p className="text-gray-200">Your order #{order.id} has been placed successfully</p>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                        {/* Transaction Summary */}
                        <div className="mb-6">
                            <h2 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2 flex items-center">
                                <svg className="w-5 h-5 text-[#0B0C52] mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Transaction Details
                            </h2>
                            <div className="space-y-3">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Transaction ID</span>
                                    <span className="font-medium text-[#0B0C52] text-sm">{order.transaction_id}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Payment Method</span>
                                    <span className="font-medium text-[#0B0C52]">{order.method}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Amount Paid</span>
                                    <span className="font-medium text-[#0B0C52]">{formattedAmount}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Date</span>
                                    <span className="font-medium text-[#0B0C52]">{order.created_at}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Status</span>
                                    <span className="font-medium text-green-600">{order.status}</span>
                                </div>
                            </div>
                        </div>

                        {/* Order Summary */}
                        <div className="mb-6">
                            <h2 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2 flex items-center">
                                <svg className="w-5 h-5 text-[#0B0C52] mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                </svg>
                                Order Summary
                            </h2>
                            <div className="space-y-3">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Order ID</span>
                                    <span className="font-medium text-[#0B0C52]">{order.order_detail.order_uid}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Customer</span>
                                    <span className="font-medium text-[#0B0C52]">{order.order_detail.customer_full_name}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Order Status</span>
                                    <span className="font-medium text-[#0B0C52]">{order.order_detail.status}</span>
                                </div>
                            </div>
                        </div>

                        {/* Shipping Address */}
                        <div className="mb-8">
                            <h2 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2 flex items-center">
                                <svg className="w-5 h-5 text-[#0B0C52] mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                Shipping Address
                            </h2>
                            <address className="not-italic text-gray-600">
                                <p className="font-medium text-[#0B0C52]">
                                    {order.order_detail.shipping_address.first_name} {order.order_detail.shipping_address.last_name}
                                </p>
                                <p>{order.order_detail.shipping_address.address_line_1}</p>
                                {order.order_detail.shipping_address.address_line_2 && (
                                    <p>{order.order_detail.shipping_address.address_line_2}</p>
                                )}
                                <p>
                                    {order.order_detail.shipping_address.city}, {order.order_detail.shipping_address.state_or_province}
                                </p>
                                <p>
                                    {order.order_detail.shipping_address.country}, {order.order_detail.shipping_address.zip_code}
                                </p>
                            </address>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-col space-y-3">
                            <button className="w-full bg-[#0B0C52] hover:bg-[#002080] text-white font-medium py-3 px-4 rounded-md transition duration-200 flex items-center justify-center" onClick={() => navigate(`/track-order/${order.order_detail
                                .order_uid}`)}>
                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                Track Order
                            </button>

                        </div>
                    </div>

                    {/* Footer */}
                    <div className="bg-gray-50 p-4 text-center border-t border-gray-200">
                        <p className="text-gray-500 text-sm">
                            Need help? Contact us at <span className="text-[#0B0C52]">support@yourlogo.com</span>
                        </p>
                        <div className="mt-2 flex justify-center space-x-4">
                            <a href="/" className="text-[#0B0C52] hover:underline text-sm">Home</a>
                            <a href="/orders" className="text-[#0B0C52] hover:underline text-sm">My Orders</a>
                            <a href="/contact" className="text-[#0B0C52] hover:underline text-sm">Contact</a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const OrderCustomization = ({ productAvailableProperties, selectedOptions, handleOptionChange }) => {
    const {
        max_available_quantity: maxQuantity = 100,
        available_fabric_materials: fabrics = [],
        available_sizes: sizes = [],
        available_printing_methods: printMethods = [],
        customization_options: customizationOptions = [],
        orientation_options: orientations = [],
        corners: cornerOptions = [],
        available_colors: colors = [],
    } = productAvailableProperties || {};
    const logFinalOptions = useCallback(debounce((options) => {

    }, 500), []);

    useEffect(() => {
        logFinalOptions(selectedOptions);
        return () => logFinalOptions.cancel();
    }, [selectedOptions, logFinalOptions]);

    const handleOptionChangeWithDebounce = (field, value) => {
        handleOptionChange(field, value);
    };

    const OptionContainer = ({ title, children }) => (
        <div className="bg-gray-50 px-2 rounded border border-gray-200 h-[120px] overflow-y-auto pb-2">
            <h3 className="text-s text-start font-medium text-[$0B0C52] bg-gray-200 mb-1 sticky top-0 z-10 py-2 px-1 mb-2">{title}</h3>
            <div className="space-y-2">
                {children}
            </div>
        </div>
    );

    return (
        <div className="bg-white rounded-lg p-4 border border-gray-200 mb-8 pb-3">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Customize Your Order</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="space-y-3">
                    <div className="bg-gray-50 px-2 rounded border border-gray-200 h-[120px] overflow-y-auto pb-2">
                        <h3 className="text-s text-start font-medium text-[$0B0C52] bg-gray-200 mb-1 sticky top-0 z-10 py-2 px-1 mb-2">Quantity</h3>
                        <div className="flex items-center gap-2">
                            <input
                                type="range"
                                min="1"
                                max={maxQuantity}
                                value={selectedOptions.quantity}
                                onChange={(e) => handleOptionChangeWithDebounce('quantity', parseInt(e.target.value, 10))}
                                className="flex-1 h-1 accent-[#0B0C52]"
                            />
                            <span className="text-xs font-medium w-6 text-center">{selectedOptions.quantity}</span>
                        </div>
                    </div>

                    {sizes.length > 0 && (
                        <OptionContainer title="Size">
                            {sizes.map((size) => (
                                <label key={size} className="flex items-center gap-1 text-xs">
                                    <input
                                        type="radio"
                                        name="size"
                                        checked={selectedOptions.size === size}
                                        onChange={() => handleOptionChangeWithDebounce('size', size)}
                                        className="h-3 w-3 accent-[#0B0C52]"
                                    />
                                    <span>{size}</span>
                                </label>
                            ))}
                        </OptionContainer>
                    )}
                </div>

                <div className="space-y-3">
                    {fabrics.length > 0 && (
                        <OptionContainer title="Fabric Material">
                            {fabrics.map((material) => (
                                <label key={material} className="flex items-center gap-1 text-xs">
                                    <input
                                        type="radio"
                                        name="fabricMaterial"
                                        checked={selectedOptions.fabricMaterial === material}
                                        onChange={() => handleOptionChangeWithDebounce('fabricMaterial', material)}
                                        className="h-3 w-3 accent-[#0B0C52]"
                                    />
                                    <span className="truncate">{material}</span>
                                </label>
                            ))}
                        </OptionContainer>
                    )}

                    {colors.length > 0 && (
                        <OptionContainer title="Color">
                            {colors.map((color) => (
                                <label key={color} className="flex items-center gap-1 text-xs">
                                    <input
                                        type="radio"
                                        name="color"
                                        checked={selectedOptions.color === color}
                                        onChange={() => handleOptionChangeWithDebounce('color', color)}
                                        className="h-3 w-3 accent-[#0B0C52]"
                                    />
                                    <div className="flex items-center gap-1">
                                        <span
                                            className="w-3 h-3 rounded-full border border-gray-300 flex-shrink-0"
                                            style={{ backgroundColor: color.toLowerCase() }}
                                        />
                                        <span className="truncate">{color}</span>
                                    </div>
                                </label>
                            ))}
                        </OptionContainer>
                    )}
                </div>

                <div className="space-y-3">
                    {orientations.length > 0 && (
                        <OptionContainer title="Orientation">
                            {orientations.map((orientation) => (
                                <label key={orientation} className="flex items-center gap-1 text-xs">
                                    <input
                                        type="radio"
                                        name="orientation"
                                        checked={selectedOptions.orientation === orientation}
                                        onChange={() => handleOptionChangeWithDebounce('orientation', orientation)}
                                        className="h-3 w-3 accent-[#0B0C52]"
                                    />
                                    <span className="truncate">{orientation}</span>
                                </label>
                            ))}
                        </OptionContainer>
                    )}

                    {cornerOptions.length > 0 && (
                        <OptionContainer title="Corner Style">
                            {cornerOptions.map((corner) => (
                                <label key={corner} className="flex items-center gap-1 text-xs">
                                    <input
                                        type="radio"
                                        name="cornerStyle"
                                        checked={selectedOptions.cornerStyle === corner}
                                        onChange={() => handleOptionChangeWithDebounce('cornerStyle', corner)}
                                        className="h-3 w-3 accent-[#0B0C52]"
                                    />
                                    <span className="truncate">{corner}</span>
                                </label>
                            ))}
                        </OptionContainer>
                    )}
                </div>

                <div className="space-y-3">
                    {printMethods.length > 0 && (
                        <OptionContainer title="Print Method">
                            {printMethods.map((method) => (
                                <label key={method} className="flex items-center gap-1 text-xs">
                                    <input
                                        type="radio"
                                        name="printMethod"
                                        checked={selectedOptions.printMethod === method}
                                        onChange={() => handleOptionChangeWithDebounce('printMethod', method)}
                                        className="h-3 w-3 accent-[#0B0C52]"
                                    />
                                    <span className="truncate">{method}</span>
                                </label>
                            ))}
                        </OptionContainer>
                    )}

                    {customizationOptions.length > 0 && (
                        <OptionContainer title="Customization Options">
                            {customizationOptions.map((option) => (
                                <label key={option} className="flex items-center gap-1 text-xs">
                                    <input
                                        type="radio"
                                        name="customizationOptions"
                                        checked={selectedOptions.customizationOptions === option}
                                        onChange={() => handleOptionChangeWithDebounce('customizationOptions', option)}
                                        className="h-3 w-3 accent-[#0B0C52]"
                                    />
                                    <span className="truncate">{option}</span>
                                </label>
                            ))}
                        </OptionContainer>
                    )}
                </div>
            </div>
        </div>
    );
};

export default function CheckoutPage() {
    const { productId, isProject, projectId } = useParams();
    const isProjectMode = isProject === 'true';
    const projectIdVValue = isProjectMode ? projectId : null;
    const location = useLocation();

    const pathToBackwardTraverse = location.state || {};

    const [paymentError, setPaymentError] = useState(null);
    const navigate = useNavigate();


    const dispatch = useDispatch();
    const [step, setStep] = useState(1);
    const [showFront, setShowFront] = useState(true);
    const [showAddressForm, setShowAddressForm] = useState(false);
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [newAddress, setNewAddress] = useState({
        firstName: '',
        lastName: '',
        street: '',
        city: '',
        state: '',
        country: '',
        zipCode: '',
        address_line_1: '',
        address_line_2: ''
    });
    const [isDefaultAddress, setIsDefaultAddress] = useState(false);
    const [shippingAddresses, setShippingAddresses] = useState([]);

    const [isTermsChecked, setIsTermsChecked] = useState(false);
    const [isCardDetailsValid, setIsCardDetailsValid] = useState(false);
    const validateCardDetails = () => {
        const cardNumber = document.querySelector('input[placeholder="Card Number"]')?.value.replace(/\s/g, '');
        const expDate = document.querySelector('input[placeholder="Expired Date (MM/YY)"]')?.value;
        const cvc = document.querySelector('input[placeholder="Cvv (MM/YY)"]')?.value;

        // Basic validation for card details
        if (cardNumber && expDate && cvc) {
            const [expMonth, expYear] = expDate.split('/');
            if (expMonth && expYear && cvc.length === 3) {
                setIsCardDetailsValid(true);
                return;
            }
        }
        setIsCardDetailsValid(false);
    };

    const handleCouponApply = (couponCode) => {
        // Update the promo code in selectedOptions
        setSelectedOptions(prev => ({
            ...prev,
            promoCode: couponCode
        }));

        // Trigger price recalculation
        debouncedPriceCalculation({
            ...selectedOptions,
            promoCode: couponCode
        });
    };

    // Add this selector for coupon data
    const { clientExpiringCoupons } = useSelector((state) => state.coupon);

    // Add this useEffect to fetch expiring coupons
    useEffect(() => {
        dispatch(thunkFetchExpiringCouponsForClient());
    }, [dispatch]);

    const [selectedOptions, setSelectedOptions] = useState({
        quantity: 1,
        fabricMaterial: '',
        size: '',
        printMethod: '',
        color: '',
        orientation: '',
        cornerStyle: '',
        customizationOptions: '',
        shippingCarrier: '',
        shippingOption: '',
        paymentMethod: '',
        promoCode: '',
    });

    const { productAvailableProperties, productAvailablePropertiesLoading, priceCalculator, priceCalculatorLoading, priceCalculatorError, projectOrProductDetailForDesign, projectOrProductDetailForDesignLoading, projectOrProductDetailForDesignError, orderSubmissionStatus, orderSubmissionError, currentOrder, paymentDetailAfterPayment, paymentDetailAfterPaymentLoading, paymentDetailAfterPaymentError } = useSelector(
        (state) => state.clientOrderAndPayment
    );

    const reconstructLayers = (design, containerWidth, containerHeight) => {
        // Match the original design dimensions from Design.jsx (500x400)
        const originalDesignWidth = 500;
        const originalDesignHeight = 400;

        // Calculate scale factors while maintaining aspect ratio
        const widthRatio = containerWidth / originalDesignWidth;
        const heightRatio = containerHeight / originalDesignHeight;
        const scale = Math.min(widthRatio, heightRatio);

        const processLayer = (layer) => {
            const baseLayer = {
                id: layer.id,
                type: layer.type,
                x: layer.x * scale,
                y: layer.y * scale,
                width: layer.width * scale,
                height: layer.height * scale,
                rotation: layer.rotation,
                flipped: layer.flipped,
                visible: layer.visible,
                zIndex: layer.z_index,
                borderColor: layer.border_color,
                borderStyle: layer.border_style,
                offsetX: layer.offset_x * scale,
                offsetY: layer.offset_y * scale,
            };

            if (layer.type === 'image' && layer.image_data) {
                return {
                    ...baseLayer,
                    src: layer.image_data.original_src || layer.image_data.src || '',
                };
            } else if (layer.type === 'text' && layer.text_data) {
                return {
                    ...baseLayer,
                    text: layer.text_data.text,
                    color: layer.text_data.color,
                };
            }
            return baseLayer;
        };

        const frontLayers = design.layers
            .filter(l => l.side === 'front')
            .map(processLayer);

        const backLayers = design.layers
            .filter(l => l.side === 'back')
            .map(processLayer);

        return { frontLayers, backLayers };
    };
    useEffect(() => {
        dispatch(fetchProductAvailablePropertiesBeforeCheckout(productId));

        if (isProjectMode) {
            dispatch(fetchProjectDetail(projectId));
        } else {
            dispatch(fetchProductDetail(productId));
        }
    }, [dispatch, productId, isProjectMode, projectId]);

    useEffect(() => {
        if (productAvailableProperties && Object.keys(productAvailableProperties).length > 0) {
            const {
                available_fabric_materials: fabrics = [],
                available_sizes: sizes = [],
                available_printing_methods: printMethods = [],
                available_colors: colors = [],
                orientation_options: orientations = [],
                customization_options: customizationOptions = [],
                corners: cornerOptions = [],
                max_available_quantity: maxQuantity = 1,
                shipping_carriers: carriers = [],
                payment_methods: paymentMethods = [],
                shipping_addresses: addresses = [],
                promo_code: promoCode = ''
            } = productAvailableProperties;

            // Set default shipping options
            let defaultCarrier = '';
            let defaultOption = '';
            if (carriers.length > 0) {
                defaultCarrier = carriers[0]?.name || '';
                const firstCarrierMethods = carriers[0]?.available_methods || [];
                defaultOption = firstCarrierMethods.includes('Standard')
                    ? 'Standard'
                    : firstCarrierMethods[0] || '';
            }

            // Set default payment method
            const defaultPaymentMethod = paymentMethods[0]?.value || '';

            const initialOptions = {
                quantity: Math.min(10, maxQuantity),
                fabricMaterial: fabrics[0] || '',
                size: sizes[0] || '',
                printMethod: printMethods[0] || '',
                color: colors[0] || '',
                orientation: orientations[0] || '',
                cornerStyle: cornerOptions[0] || '',
                customizationOptions: customizationOptions[0] || '',
                shippingCarrier: defaultCarrier,
                shippingOption: defaultOption,
                paymentMethod: defaultPaymentMethod,
                promoCode: promoCode || ''
            };

            // First set all the default values in state
            setSelectedOptions(initialOptions);

            // Immediately calculate price with the initial options
            const initialFormData = {
                productId,
                quantity: initialOptions.quantity,
                fabric_material: initialOptions.fabricMaterial,
                size: initialOptions.size,
                print_method: initialOptions.printMethod,
                color: initialOptions.color,
                orientation: initialOptions.orientation,
                corner_style: initialOptions.cornerStyle,
                customization_options: initialOptions.customizationOptions,
                shipping_carrier: initialOptions.shippingCarrier,
                shipping_option: initialOptions.shippingOption,
                payment_method: initialOptions.paymentMethod,
                promo_code: initialOptions.promoCode
            };
            dispatch(priceCalculatorBeforeCheckout({ productId, formData: initialFormData }));

            // Set default address
            const defaultAddress = addresses.find(addr => addr.is_default) || addresses[0];
            if (defaultAddress) {
                setSelectedAddress(defaultAddress.id);
            }
        }
    }, [productAvailableProperties, dispatch, productId]);

    const debouncedPriceCalculation = useCallback(
        debounce((options) => {
            const {
                cornerStyle,
                fabricMaterial,
                printMethod,
                customizationOptions,
                shippingCarrier,
                shippingOption,
                paymentMethod,
                promoCode,
                ...rest
            } = options;

            const formattedData = {
                ...rest,
                corner_style: cornerStyle,
                fabric_material: fabricMaterial,
                print_method: printMethod,
                customization_options: customizationOptions,
                shipping_carrier: shippingCarrier,
                shipping_option: shippingOption,
                payment_method: paymentMethod,
                promo_code: promoCode,
                productId
            };

            dispatch(priceCalculatorBeforeCheckout({ productId, formData: formattedData }));
        }, 500),
        [dispatch, productId]
    );

    useEffect(() => {
        if (productAvailableProperties?.shipping_addresses) {
            setShippingAddresses(productAvailableProperties.shipping_addresses);
        }
    }, [productAvailableProperties]);

    // const handleAddressSelect = (addressId) => {
    //     setSelectedAddress(addressId);
    //     setShowAddressForm(false);
    // };

    const handleCancelPayment = () => {
        navigate(-1);

    }
    const handleToggleDefaultAddress = async (addressId) => {
        try {
            // Dispatch the action to toggle the default status
            await dispatch(toggleShippingAddressStatus({ shippingAddressId: addressId })).unwrap();

            // Update the local state to reflect the change
            setShippingAddresses((prevAddresses) =>
                prevAddresses.map((address) => ({
                    ...address,
                    is_default: address.id === addressId, // Set the selected address as default
                }))
            );
        } catch (error) {
            console.error('Failed to toggle default address:', error);
        }
    };

    // Add this right after other handler functions
    const handlePaymentSubmit = async () => {
        // Collect all necessary data
        const formData = {
            product_id: productId,
            is_project: isProjectMode,
            project_id: projectIdVValue,
            promo_code: selectedOptions.promoCode,

            // Customization details
            quantity: selectedOptions.quantity,
            fabric_material: selectedOptions.fabricMaterial,
            size: selectedOptions.size,
            print_method: selectedOptions.printMethod,
            color: selectedOptions.color,
            orientation: selectedOptions.orientation,
            corner_style: selectedOptions.cornerStyle,
            customization_options: selectedOptions.customizationOptions,

            // Shipping details
            shipping_carrier: selectedOptions.shippingCarrier,
            shipping_option: selectedOptions.shippingOption,

            // 

            // Payment details
            card_number: document.querySelector('input[placeholder="Card Number"]')?.value.replace(/\s/g, ''),
            exp_month: document.querySelector('input[placeholder="Expired Date (MM/YY)"]')?.value.split('/')[0],
            exp_year: document.querySelector('input[placeholder="Expired Date (MM/YY)"]')?.value.split('/')[1],
            cvc: document.querySelector('input[placeholder="Cvv (MM/YY)"]')?.value,


            address_id: selectedAddress,

        };


        try {
            setPaymentError(null);

            const result = await dispatch(submitOrder(formData)).unwrap();

            if (result.status === 'success' && result.payment_intent_id) {
                // Pass the payment_intent_id to fetchPaymentDetailAfterPayment
                dispatch(fetchPaymentDetailAfterPayment(result.payment_intent_id));
                setStep(2);
            } else {
                throw new Error('Payment intent ID not found in the response.');
            }
            // Move to confirmation step
        } catch (error) {
            console.error('Failed to submit order:', error);
            setPaymentError('Payment failed. Please check your card details or try again.');
        }
    };

    const handlePromoCodeChange = (value) => {
        setSelectedOptions(prev => ({
            ...prev,
            promoCode: value
        }));

        // Only trigger calculation when field loses focus (via onBlur)
    };

    const handleOptionChange = (field, value) => {
        setSelectedOptions(prev => {
            const newOptions = {
                ...prev,
                [field]: value
            };
            // Use the updated state immediately for calculation
            debouncedPriceCalculation(newOptions);
            return newOptions;
        });
    };

    const handleAddressSelect = (addressId) => {
        setSelectedAddress(addressId);
        setShowAddressForm(false);
    };

    const handleNewAddressSubmit = async (e) => {
        e.preventDefault();

        // Create the new address object
        const addressData = {
            first_name: newAddress.firstName,
            last_name: newAddress.lastName,
            street_address: newAddress.street,
            city: newAddress.city,
            state_or_province: newAddress.state,
            country: newAddress.country,
            zip_code: newAddress.zipCode,
            is_default: isDefaultAddress,
            address_line_1: newAddress.address_line_1,
            address_line_2: newAddress.address_line_2

        };

        try {
            // Dispatch action to save the address (you'll need to implement this in your slice)
            const result = await dispatch(addShippingAddress(addressData)).unwrap();
            setShippingAddresses((prev) => [...prev, result]);
            // Add the new address to the list and select it
            setSelectedAddress(result.id);
            setShowAddressForm(false);

            // Reset form
            setNewAddress({
                firstName: '',
                lastName: '',
                street: '',
                city: '',
                state: '',
                country: '',
                zipCode: '',
                address_line_1: '',
                address_line_2: ''

            });
            setIsDefaultAddress(false);
        } catch (error) {
            console.error('Failed to save address:', error);
        }
    };

    const handleAddressInputChange = (e) => {
        const { name, value } = e.target;
        setNewAddress(prev => ({
            ...prev,
            [name]: value
        }));
    };


    // useEffect(() => {
    //     if (step === 2) {
    //         // Fetch payment details when step is 2
    //         dispatch(fetchPaymentDetailAfterPayment());
    //     }
    // }, [step, dispatch]);

    if (step === 2 && paymentDetailAfterPaymentLoading) {
        return (
            <div className="bg-[#EEE8E8] min-h-screen py-8">
                <div className="max-w-screen-xl mx-auto px-4">
                    {/* Shimmer for Steps */}
                    <div className="flex items-center justify-center mb-8">
                        <div className="text-center">
                            <div className="shimmer-wrapper w-10 h-10 rounded-full mx-auto"></div>
                            <div className="shimmer-wrapper w-20 h-4 mt-2 mx-auto"></div>
                        </div>
                        <div className="flex-2 w-16 border-t-2 border-gray-300"></div>
                        <div className="text-center">
                            <div className="shimmer-wrapper w-10 h-10 rounded-full mx-auto"></div>
                            <div className="shimmer-wrapper w-20 h-4 mt-2 mx-auto"></div>
                        </div>
                    </div>
                    <div className="text-center">
                        <p>Loading confirmation details...</p>
                    </div>
                </div>
            </div>
        );
    }


    if (step === 2) {
        // Render the SuccessfulPaymentConfirmation component when step is 2
        // setLoadingConfirmation
        return <SuccessfulPaymentConfirmation orderData={paymentDetailAfterPayment} navigate={navigate} />;
    }


    if (productAvailablePropertiesLoading || projectOrProductDetailForDesignLoading) {
        return (

            <div className="bg-[#EEE8E8] min-h-screen py-8 ">
                <div className="max-w-screen-xl mx-auto px-4">
                    {/* Shimmer for Steps */}
                    <div className="flex items-center justify-center mb-8">
                        <div className="text-center">
                            <div className="shimmer-wrapper w-10 h-10 rounded-full mx-auto"></div>
                            <div className="shimmer-wrapper w-20 h-4 mt-2 mx-auto"></div>
                        </div>
                        <div className="flex-2 w-16 border-t-2 border-gray-300"></div>
                        <div className="text-center">
                            <div className="shimmer-wrapper w-10 h-10 rounded-full mx-auto"></div>
                            <div className="shimmer-wrapper w-20 h-4 mt-2 mx-auto"></div>
                        </div>
                    </div>

                    {/* Shimmer for Customization Section */}
                    <div className="bg-white rounded-lg p-4 border border-gray-200 mb-8 pb-3">
                        <div className="shimmer-wrapper w-40 h-6 mb-4"></div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                            {Array(4)
                                .fill(0)
                                .map((_, index) => (
                                    <div key={index} className="space-y-3">
                                        <div className="shimmer-wrapper h-[120px] rounded"></div>
                                        <div className="shimmer-wrapper h-[120px] rounded"></div>
                                    </div>
                                ))}
                        </div>
                    </div>

                    <div className="flex flex-col md:flex-row gap-8">
                        {/* Shimmer for Preview Section */}
                        <div className="flex-1 bg-white rounded-md p-6 relative">
                            <div className="mb-6">
                                <div className="relative flex justify-center items-center mb-4">
                                    <div className="shimmer-wrapper w-[400px] h-[300px] rounded-md"></div>
                                </div>
                                <div className="flex justify-center gap-2">
                                    <div className="shimmer-wrapper w-20 h-8 rounded-full"></div>
                                    <div className="shimmer-wrapper w-20 h-8 rounded-full"></div>
                                </div>
                            </div>
                            <div className="space-y-4 px-6 py-8">
                                {Array(4)
                                    .fill(0)
                                    .map((_, index) => (
                                        <div key={index} className="flex gap-3">
                                            <div className="shimmer-wrapper w-6 h-6 rounded-full"></div>
                                            <div className="shimmer-wrapper w-full h-6"></div>
                                        </div>
                                    ))}
                            </div>
                        </div>

                        {/* Shimmer for Order Summary */}
                        <div className="w-full md:w-[400px]">
                            <div className="bg-[#0B0C52] text-white text-center py-2 rounded-t-md">
                                <div className="shimmer-wrapper w-40 h-6 mx-auto"></div>
                            </div>
                            <div className="bg-white rounded-b-md p-6">
                                <div className="space-y-4">
                                    <div className="shimmer-wrapper h-6 w-full rounded"></div>
                                    <div className="shimmer-wrapper h-6 w-full rounded"></div>
                                    <div className="shimmer-wrapper h-6 w-full rounded"></div>
                                </div>
                                <hr className="border-gray-300 my-4" />
                                <div className="shimmer-wrapper h-6 w-full rounded"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
    return (
        <div> <Breadcrumbs pathToBackwardTraverse={pathToBackwardTraverse} />
            <div className="bg-[#EEE8E8] min-h-screen py-8 relative">

                {orderSubmissionStatus === 'loading' && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                        <div className="bg-white p-6 rounded-md shadow-lg text-center">
                            <p className="text-lg font-semibold text-gray-800 mb-4">
                                Please don't do anything other than waiting
                            </p>
                            <div className="flex justify-center">
                                <svg
                                    className="animate-spin h-8 w-8 text-[#0B0C52]"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                    ></circle>
                                    <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                </svg>
                            </div>
                        </div>
                    </div>
                )}

                <div className="max-w-screen-xl mx-auto px-4">
                    <div className="flex items-center justify-center mb-8">
                        <div className="text-center">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center mx-auto ${step === 1 ? 'bg-blue-600 text-white' : 'bg-gray-400 text-white opacity-50'}`}>
                                1
                            </div>
                            <p className={`text-sm mt-2 font-semibold ${step === 1 ? 'text-black' : 'opacity-50'}`}>
                                Check Out
                            </p>
                        </div>
                        <div className="flex-2 w-16 border-t-2 border-gray-300"></div>
                        <div className="text-center">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center mx-auto ${step === 2 ? 'bg-blue-600 text-white' : 'bg-gray-400 text-white opacity-50'}`}>
                                2
                            </div>
                            <p className={`text-sm mt-2 font-semibold ${step === 2 ? 'text-black' : 'opacity-50'}`}>
                                Confirmation
                            </p>
                        </div>
                    </div>

                    <OrderCustomization
                        productAvailableProperties={productAvailableProperties}
                        selectedOptions={selectedOptions}
                        handleOptionChange={handleOptionChange}
                    />

                    <div className="flex flex-col md:flex-row gap-8">

                        <div className="flex-1 bg-white rounded-md p-6 relative">
                            <div className="mb-6">
                                <div className="relative flex justify-center items-center mb-4">
                                    {/* Preview Container - Matches Design.jsx preview dimensions (400x300) */}
                                    <div className="relative w-[400px] h-[300px] bg-white shadow-md mx-auto shadow-lg rounded-md shadow-[#0B0C52] ">
                                        {projectOrProductDetailForDesignLoading ? (
                                            <div className="w-full h-full shimmer-wrapper shimmer"> </div>

                                        ) : isProjectMode ? (
                                            projectOrProductDetailForDesign ? (
                                                <div className="relative w-full h-full">
                                                    {/* Front View */}
                                                    {showFront && (
                                                        <div className="absolute w-full h-full">
                                                            {projectOrProductDetailForDesign.product?.images?.front_image && (
                                                                <img
                                                                    src={projectOrProductDetailForDesign.product.images.front_image}
                                                                    alt="Product front"
                                                                    className="absolute w-full h-full object-contain"
                                                                    style={{ zIndex: 1 }}
                                                                />
                                                            )}
                                                            {reconstructLayers(projectOrProductDetailForDesign, 400, 300).frontLayers.map((layer) => (
                                                                layer.visible && (
                                                                    <div
                                                                        key={layer.id}
                                                                        className="absolute"
                                                                        style={{
                                                                            top: `${layer.y}px`,
                                                                            left: `${layer.x}px`,
                                                                            width: `${layer.width}px`,
                                                                            height: `${layer.height}px`,
                                                                            transform: `rotate(${layer.rotation}deg) scaleX(${layer.flipped ? -1 : 1})`,
                                                                            zIndex: layer.zIndex + 2,
                                                                            overflow: 'hidden',
                                                                        }}
                                                                    >
                                                                        {layer.type === 'text' ? (
                                                                            <div
                                                                                className="w-full h-full flex items-center justify-center p-1"
                                                                                style={{
                                                                                    color: layer.color,
                                                                                    fontWeight: 'bold',
                                                                                    borderRadius: '2px',
                                                                                }}
                                                                            >
                                                                                {layer.text}
                                                                            </div>
                                                                        ) : (
                                                                            layer.src && (
                                                                                <img
                                                                                    src={layer.src}
                                                                                    alt="Layer"
                                                                                    className="w-full h-full object-cover"
                                                                                />
                                                                            )
                                                                        )}
                                                                    </div>
                                                                )
                                                            ))}
                                                        </div>
                                                    )}

                                                    {/* Back View */}
                                                    {!showFront && (
                                                        <div className="absolute w-full h-full">
                                                            {projectOrProductDetailForDesign.product?.images?.back_image && (
                                                                <img
                                                                    src={projectOrProductDetailForDesign.product.images.back_image ||
                                                                        projectOrProductDetailForDesign.product.images.front_image}
                                                                    alt="Product back"
                                                                    className="absolute w-full h-full object-contain"
                                                                    style={{ zIndex: 1 }}
                                                                />
                                                            )}
                                                            {reconstructLayers(projectOrProductDetailForDesign, 400, 300).backLayers.map((layer) => (
                                                                layer.visible && (
                                                                    <div
                                                                        key={layer.id}
                                                                        className="absolute"
                                                                        style={{
                                                                            top: `${layer.y}px`,
                                                                            left: `${layer.x}px`,
                                                                            width: `${layer.width}px`,
                                                                            height: `${layer.height}px`,
                                                                            transform: `rotate(${layer.rotation}deg) scaleX(${layer.flipped ? -1 : 1})`,
                                                                            zIndex: layer.zIndex + 2,
                                                                            overflow: 'hidden',
                                                                        }}
                                                                    >
                                                                        {layer.type === 'text' ? (
                                                                            <div
                                                                                className="w-full h-full flex items-center justify-center p-1"
                                                                                style={{
                                                                                    color: layer.color,
                                                                                    fontWeight: 'bold',
                                                                                    borderRadius: '2px',
                                                                                }}
                                                                            >
                                                                                {layer.text}
                                                                            </div>
                                                                        ) : (
                                                                            layer.src && (
                                                                                <img
                                                                                    src={layer.src}
                                                                                    alt="Layer"
                                                                                    className="w-full h-full object-cover"
                                                                                />
                                                                            )
                                                                        )}
                                                                    </div>
                                                                )
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <p className="text-gray-500">No project data available</p>
                                                </div>
                                            )
                                        ) : projectOrProductDetailForDesign ? (
                                            <div className="relative w-full h-full bg-gray-100 rounded-md overflow-hidden">
                                                <div className={`flipper ${!showFront ? 'flipped' : ''}`}>
                                                    {/* Front View */}
                                                    <div className="front absolute w-full h-full">
                                                        {projectOrProductDetailForDesign.template?.media_files?.[0]?.front_image ||
                                                            projectOrProductDetailForDesign.images?.front_image ? (
                                                            <img
                                                                src={projectOrProductDetailForDesign.template?.media_files?.[0]?.front_image ||
                                                                    projectOrProductDetailForDesign.images?.front_image}
                                                                alt={projectOrProductDetailForDesign.product_name}
                                                                className="w-full h-full object-contain"
                                                            />
                                                        ) : (
                                                            <div className="w-full h-full flex items-center justify-center">
                                                                <p className="text-gray-500">No preview available</p>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Back View */}
                                                    <div className="back absolute w-full h-full">
                                                        {projectOrProductDetailForDesign.template?.media_files?.[0]?.back_image ||
                                                            projectOrProductDetailForDesign.images?.back_image ? (
                                                            <img
                                                                src={projectOrProductDetailForDesign.template?.media_files?.[0]?.back_image ||
                                                                    projectOrProductDetailForDesign.images?.back_image}
                                                                alt={`${projectOrProductDetailForDesign.product_name} (back)`}
                                                                className="w-full h-full object-contain"
                                                            />
                                                        ) : (
                                                            <div className="w-full h-full flex items-center justify-center">
                                                                <p className="text-gray-500">No preview available</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center">
                                                <p className="text-gray-500">No product data available</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Front/Back Toggle - Matches Design.jsx styling */}
                                <div className="flex justify-center gap-2">
                                    <button
                                        className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 ${showFront
                                            ? 'bg-[#0B0C52] text-white shadow-md transform scale-105'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                        onClick={() => setShowFront(true)}
                                    >
                                        <span className="flex items-center justify-center gap-1">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                <path d="M21 12H3M12 21V3M12 21L3 12M12 21L21 12M12 3L3 12M12 3L21 12" strokeWidth="2" strokeLinecap="round" />
                                            </svg>
                                            Front
                                        </span>
                                    </button>
                                    <button
                                        className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 ${!showFront
                                            ? 'bg-[#0B0C52] text-white shadow-md transform scale-105'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                        onClick={() => setShowFront(false)}
                                    >
                                        <span className="flex items-center justify-center gap-1">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                <path d="M12 3H3V21H21V12M12 21V3M12 21L21 12M12 3L21 12" strokeWidth="2" strokeLinecap="round" />
                                            </svg>
                                            Back
                                        </span>
                                    </button>
                                </div>
                            </div>
                            <div className="mb-6">
                                <div className="flex items-center gap-3">
                                    {/* Icon based on project/product type */}
                                    {isProjectMode ? (
                                        <svg className="w-6 h-6 text-[#0B0C52]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                        </svg>
                                    ) : (
                                        <svg className="w-6 h-6 text-[#0B0C52]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                        </svg>
                                    )}

                                    <h2 className="text-3xl font-bold text-gray-800 tracking-tight">
                                        {isProjectMode
                                            ? projectOrProductDetailForDesign?.name || 'My Project'
                                            : projectOrProductDetailForDesign?.product_name || 'Our Product'}
                                    </h2>
                                </div>

                                {/* Subtitle with category */}
                                <p className="mt-1 text-lg text-gray-500">
                                    {isProjectMode ? (
                                        <>
                                            Custom {projectOrProductDetailForDesign?.product?.category?.name.toLowerCase() || 'design'} •
                                            Created {projectOrProductDetailForDesign?.created_at ? new Date(projectOrProductDetailForDesign.created_at).toLocaleDateString() : 'recently'}
                                        </>
                                    ) : (
                                        <>
                                            {projectOrProductDetailForDesign?.category?.name || 'Product'} •
                                            {projectOrProductDetailForDesign?.sub_category?.name || 'Item'}
                                        </>
                                    )}
                                </p>
                            </div>

                            <div className="space-y-4 px-6 py-8">
                                {isProjectMode ? (
                                    <>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {/* Project Name */}
                                            <div className="flex">
                                                <div className="flex items-center text-gray-400 mr-3">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                                                    </svg>
                                                </div>
                                                <div className="flex-1">
                                                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-0.5">
                                                        Project Name
                                                    </div>
                                                    <div className="text-sm font-medium text-gray-800">
                                                        {projectOrProductDetailForDesign?.name || 'Not specified'}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Created Date */}
                                            <div className="flex">
                                                <div className="flex items-center text-gray-400 mr-3">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                    </svg>
                                                </div>
                                                <div className="flex-1">
                                                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-0.5">
                                                        Created
                                                    </div>
                                                    <div className="text-sm font-medium text-gray-800">
                                                        {projectOrProductDetailForDesign?.created_at ?
                                                            new Date(projectOrProductDetailForDesign.created_at).toLocaleDateString('en-US', {
                                                                year: 'numeric',
                                                                month: 'short',
                                                                day: 'numeric'
                                                            }) : 'Not specified'}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Base Product */}
                                            <div className="flex">
                                                <div className="flex items-center text-gray-400 mr-3">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                                    </svg>
                                                </div>
                                                <div className="flex-1">
                                                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-0.5">
                                                        Base Product
                                                    </div>
                                                    <div className="text-sm font-medium text-gray-800">
                                                        {projectOrProductDetailForDesign?.product?.product_name || 'Not specified'}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Product Price */}
                                            <div className="flex">
                                                <div className="flex items-center text-gray-400 mr-3">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                                    </svg>
                                                </div>
                                                <div className="flex-1">
                                                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-0.5">
                                                        Product Price
                                                    </div>
                                                    <div className="text-sm font-medium text-gray-800">
                                                        {projectOrProductDetailForDesign?.product?.price ?
                                                            `$${projectOrProductDetailForDesign.product.price}` : 'Not specified'}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Category */}
                                        <div className="pt-2 border-t border-gray-100">
                                            <div className="flex">
                                                <div className="flex items-center text-gray-400 mr-3">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                                                    </svg>
                                                </div>
                                                <div className="flex-1">
                                                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-0.5">
                                                        Category
                                                    </div>
                                                    <div className="text-sm font-medium text-gray-800">
                                                        {projectOrProductDetailForDesign?.product?.category?.name || 'Not specified'} /
                                                        {projectOrProductDetailForDesign?.product?.sub_category?.name || 'Not specified'}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {/* Product Name */}
                                            <div className="flex">
                                                <div className="flex items-center text-gray-400 mr-3">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                                    </svg>
                                                </div>
                                                <div className="flex-1">
                                                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-0.5">
                                                        Product Name
                                                    </div>
                                                    <div className="text-sm font-medium text-gray-800">
                                                        {projectOrProductDetailForDesign?.product_name || 'Not specified'}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Price */}
                                            <div className="flex">
                                                <div className="flex items-center text-gray-400 mr-3">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                                    </svg>
                                                </div>
                                                <div className="flex-1">
                                                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-0.5">
                                                        Price
                                                    </div>
                                                    <div className="text-sm font-medium text-gray-800">
                                                        {projectOrProductDetailForDesign?.price ?
                                                            `$${projectOrProductDetailForDesign.price}` : 'Not specified'}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Stock Level */}
                                            <div className="flex">
                                                <div className="flex items-center text-gray-400 mr-3">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                                                    </svg>
                                                </div>
                                                <div className="flex-1">
                                                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-0.5">
                                                        Stock Level
                                                    </div>
                                                    <div className={`text-sm font-medium ${projectOrProductDetailForDesign?.stock_level === 'Low' ?
                                                        'text-red-600' : 'text-gray-800'}`}>
                                                        {projectOrProductDetailForDesign?.stock_level || 'Not specified'}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Status */}
                                            <div className="flex">
                                                <div className="flex items-center text-gray-400 mr-3">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                </div>
                                                <div className="flex-1">
                                                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-0.5">
                                                        Status
                                                    </div>
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${projectOrProductDetailForDesign?.status === 'ACTIVE' ? 'text-green-600 bg-green-50' :
                                                        projectOrProductDetailForDesign?.status === 'INACTIVE' ? 'text-red-600 bg-red-50' :
                                                            projectOrProductDetailForDesign?.status === 'DRAFT' ? 'text-yellow-600 bg-yellow-50' :
                                                                'text-gray-600 bg-gray-50'
                                                        }`}>
                                                        {projectOrProductDetailForDesign?.status || 'Not specified'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Category and Materials */}
                                        <div className="pt-2 border-t border-gray-100 space-y-3">
                                            {/* Category */}
                                            <div className="flex">
                                                <div className="flex items-center text-gray-400 mr-3">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                                                    </svg>
                                                </div>
                                                <div className="flex-1">
                                                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-0.5">
                                                        Category
                                                    </div>
                                                    <div className="text-sm font-medium text-gray-800">
                                                        {projectOrProductDetailForDesign?.category?.name || 'Not specified'} /
                                                        {projectOrProductDetailForDesign?.sub_category?.name || 'Not specified'}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Available Materials */}
                                            <div className="flex">
                                                <div className="flex items-center text-gray-400 mr-3">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                                    </svg>
                                                </div>
                                                <div className="flex-1">
                                                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-0.5">
                                                        Available Materials
                                                    </div>
                                                    <div className="text-sm font-medium text-gray-800">
                                                        {projectOrProductDetailForDesign?.available_fabric_materials?.join(', ') || 'Not specified'}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>


                            <div className="flex justify-center absolute bottom-0 left-0 w-full p-4 bg-white border-t border-gray-100">
                                <button className="min-w-[180px] px-8 py-2.5 rounded-lg transition-all duration-200 font-medium flex items-center justify-center space-x-2 
                    bg-red-500 text-white hover:bg-red-600 active:bg-red-700 
                    border border-red-500 hover:border-red-600 shadow-sm hover:shadow-md">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-5 w-5"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        strokeWidth={2}
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                    <span onClick={handleCancelPayment} className="text-sm font-semibold tracking-tight">Cancel</span>
                                </button>
                            </div>
                        </div>

                        <div className="w-full md:w-[400px]">
                            <div className="bg-[#0B0C52] text-white text-center py-2 rounded-t-md">
                                <h2 className="text-lg font-bold">checkout</h2>
                            </div>
                            <div className="bg-white rounded-b-md p-6">
                                <div className="bg-white rounded-b-md p-6">
                                    <h3 className="font-bold text-lg mb-2">Order Summary</h3>
                                    {priceCalculatorLoading ? (
                                        <div className="space-y-4">
                                            <div className="shimmer-wrapper h-6 w-full rounded"></div>
                                            <div className="shimmer-wrapper h-6 w-full rounded"></div>
                                            <div className="shimmer-wrapper h-6 w-full rounded"></div>
                                        </div>
                                    ) : (
                                        <div className="space-y-2 mb-4 text-sm">
                                            {/* Product Price */}
                                            <div className="flex justify-between">
                                                <span>
                                                    Product ({priceCalculator?.main_price?.product?.quantity} × $
                                                    {priceCalculator?.main_price?.product?.per_1_price?.toFixed(2)})
                                                </span>
                                                <span>${priceCalculator?.main_price?.product?.total_price?.toFixed(2)}</span>
                                            </div>

                                            {/* Shipping Price */}
                                            <div className="flex justify-between">
                                                <span>
                                                    Shipping ({priceCalculator?.main_price?.shipping?.quantity} × $
                                                    {priceCalculator?.main_price?.shipping?.per_1_price?.toFixed(2)})
                                                </span>
                                                <span>${priceCalculator?.main_price?.shipping?.total_price?.toFixed(2)}</span>
                                            </div>

                                            {/* Coupon Discounts */}
                                            {priceCalculator?.coupon_discount > 0 && (
                                                <div className="flex justify-between text-green-600">
                                                    <span>Coupon Discount</span>
                                                    <span>-${priceCalculator.coupon_discount.toFixed(2)}</span>
                                                </div>
                                            )}

                                            {/* Shipping Discount */}
                                            {priceCalculator?.shipping_discount > 0 && (
                                                <div className="flex justify-between text-green-600">
                                                    <span>Shipping Discount</span>
                                                    <span>-${priceCalculator.shipping_discount.toFixed(2)}</span>
                                                </div>
                                            )}

                                            {/* Invalid Coupon Messages */}
                                            {priceCalculator?.invalid_coupons?.length > 0 && (
                                                <div className="text-red-500 text-xs">
                                                    {priceCalculator.invalid_coupons.map((coupon, index) => (
                                                        <div key={index}>
                                                            {coupon.code}: {coupon.message}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Price Details (collapsible) */}
                                            {priceCalculator?.payment_detail && (
                                                <div className="mt-2">
                                                    <details className="group">
                                                        <summary className="flex justify-between items-center cursor-pointer text-[#0B0C52] font-medium">
                                                            <span>Show price details</span>
                                                            <svg
                                                                className="w-4 h-4 group-open:rotate-180 transition-transform"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                viewBox="0 0 24 24"
                                                            >
                                                                <path
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    strokeWidth="2"
                                                                    d="M19 9l-7 7-7-7"
                                                                />
                                                            </svg>
                                                        </summary>
                                                        <div className="mt-2 pl-2 border-l-2 border-gray-200 space-y-1">
                                                            {Object.entries(priceCalculator.payment_detail).map(([key, value]) => (
                                                                <div key={key} className="flex justify-between text-xs text-gray-600">
                                                                    <span>
                                                                        {key} ({value.quantity} × $
                                                                        {value.per_1_price?.toFixed(2)})
                                                                    </span>
                                                                    <span>${value.total_price?.toFixed(2)}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </details>
                                                </div>
                                            )}

                                            <hr className="border-gray-300 my-2" />

                                            {/* Subtotal Before Coupons */}
                                            <div className="flex justify-between">
                                                <span>Subtotal</span>
                                                <span>${priceCalculator?.subtotal_before_coupons?.toFixed(2)}</span>
                                            </div>

                                            {/* Tax */}
                                            {priceCalculator?.tax > 0 && (
                                                <div className="flex justify-between">
                                                    <span>Tax</span>
                                                    <span>${priceCalculator.tax.toFixed(2)}</span>
                                                </div>
                                            )}

                                            {/* Total Price */}
                                            <div className="flex justify-between font-semibold">
                                                <span>Total</span>
                                                <span>${priceCalculator?.total_price?.toFixed(2)}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="mb-4">
                                    <h3 className="font-bold text-lg mb-2">Promo Code</h3>
                                    <input
                                        type="text"
                                        placeholder="Promo Code"
                                        className="border border-gray-300 p-2 w-full"
                                        value={selectedOptions.promoCode}
                                        onChange={(e) => handlePromoCodeChange(e.target.value)}
                                        onBlur={() => {
                                            // Only trigger calculation when user leaves the field
                                            if (selectedOptions && selectedOptions.promoCode && selectedOptions.promoCode.trim() !== '') {
                                                debouncedPriceCalculation({
                                                    ...selectedOptions,
                                                    promoCode: selectedOptions.promoCode.trim()
                                                });
                                            }
                                        }}
                                    />
                                </div>
                                <h3 className="font-bold text-lg mb-2">Payment Method</h3>
                                <div className="flex flex-wrap gap-4 mb-4">
                                    {productAvailableProperties?.payment_methods?.map((method) => (
                                        <label key={method.value} className="flex items-center gap-1 text-sm">
                                            <input
                                                type="radio"
                                                name="payment"
                                                checked={selectedOptions.paymentMethod === method.value}
                                                onChange={() => handleOptionChange('paymentMethod', method.value)}
                                                className="h-4 w-4 accent-[#0B0C52]"
                                            />
                                            <span>{method.name}</span>
                                        </label>
                                    ))}
                                </div>

                                {productAvailableProperties?.shipping_carriers?.length > 0 && (
                                    <>
                                        <h3 className="font-bold text-lg mb-2 mt-4">Shipping Method</h3>
                                        <div className="mb-4">
                                            <div className="mb-3">
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Carrier</label>
                                                <div className="grid grid-cols-2 gap-2">
                                                    {productAvailableProperties.shipping_carriers.map((carrier) => (
                                                        <label
                                                            key={carrier.name}
                                                            className={`flex items-center gap-2 p-2 border rounded-md cursor-pointer ${selectedOptions.shippingCarrier === carrier.name
                                                                ? 'border-[#0B0C52] bg-blue-50'
                                                                : 'border-gray-300'
                                                                }`}
                                                        >
                                                            <input
                                                                type="radio"
                                                                name="shippingCarrier"
                                                                checked={selectedOptions.shippingCarrier === carrier.name}
                                                                onChange={() => {
                                                                    const newCarrier = carrier.name;
                                                                    const newOption = carrier.available_methods.includes('Standard')
                                                                        ? 'Standard'
                                                                        : carrier.available_methods[0];

                                                                    // Update UI immediately
                                                                    setSelectedOptions(prev => ({
                                                                        ...prev,
                                                                        shippingCarrier: newCarrier,
                                                                        shippingOption: newOption
                                                                    }));

                                                                    // Trigger price calculation
                                                                    debouncedPriceCalculation({
                                                                        ...selectedOptions,
                                                                        shippingCarrier: newCarrier,
                                                                        shippingOption: newOption
                                                                    });
                                                                }}
                                                                className="h-4 w-4 accent-[#0B0C52]"
                                                            />
                                                            <span className="text-sm">{carrier.name}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Shipping Option</label>
                                                <div className="grid grid-cols-2 gap-2">
                                                    {productAvailableProperties.shipping_carriers
                                                        .find(c => c.name === selectedOptions.shippingCarrier)
                                                        ?.available_methods.map((method) => (
                                                            <label
                                                                key={method}
                                                                className={`flex items-center gap-2 p-2 border rounded-md cursor-pointer ${selectedOptions.shippingOption === method
                                                                    ? 'border-[#0B0C52] bg-blue-50'
                                                                    : 'border-gray-300'
                                                                    }`}
                                                            >
                                                                <input
                                                                    type="radio"
                                                                    name="shippingOption"
                                                                    checked={selectedOptions.shippingOption === method}
                                                                    onChange={() => {
                                                                        // Update UI immediately
                                                                        setSelectedOptions(prev => ({
                                                                            ...prev,
                                                                            shippingOption: method
                                                                        }));

                                                                        // Trigger price calculation
                                                                        debouncedPriceCalculation({
                                                                            ...selectedOptions,
                                                                            shippingOption: method
                                                                        });
                                                                    }}
                                                                    className="h-4 w-4 accent-[#0B0C52]"
                                                                />
                                                                <span className="text-sm">{method}</span>
                                                            </label>
                                                        ))}
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}

                                <h3 className="font-bold text-lg mb-2">Card Details</h3>
                                <div className="grid grid-cols-1 gap-2 mb-4 text-sm">
                                    <input
                                        type="text"
                                        placeholder="Card Number"
                                        className="border border-gray-300 p-2 w-full"
                                        onChange={validateCardDetails}
                                        onBlur={validateCardDetails}
                                    />
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            placeholder="Expired Date (MM/YY)"
                                            onChange={validateCardDetails}
                                            onBlur={validateCardDetails}
                                            className="border border-gray-300 p-2 w-full"
                                        />
                                        <input
                                            type="text"
                                            placeholder="Cvv (MM/YY)"
                                            className="border border-gray-300 p-2 w-full"
                                            onChange={validateCardDetails}
                                            onBlur={validateCardDetails}
                                        />
                                    </div>
                                    {/* <input
                                        type="text"
                                        placeholder="Promo Code"
                                        className="border border-gray-300 p-2 w-full"
                                        onChange={validateCardDetails}
                                        onBlur={validateCardDetails}
                                    /> */}
                                </div>


                                <h3 className="font-bold text-lg mb-2">Shipping Information</h3>
                                {!showAddressForm ? (
                                    <>
                                        {shippingAddresses.length > 0 && (
                                            <div className="mb-4 space-y-2">
                                                {shippingAddresses.map((address) => (
                                                    <div
                                                        key={address.id}
                                                        className={`p-3 border rounded-md cursor-pointer ${selectedAddress === address.id
                                                            ? 'border-[#0B0C52] bg-blue-50'
                                                            : 'border-gray-300'
                                                            }`}
                                                        onClick={() => setSelectedAddress(address.id)}
                                                    >
                                                        <div className="flex justify-between">
                                                            <span className="font-medium">{address.first_name} {address.last_name}</span>
                                                            {address.is_default && (
                                                                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                                                    Default
                                                                </span>
                                                            )}
                                                        </div>
                                                        <p className="text-sm">{address.street_address}</p>
                                                        <p className="text-sm">{address.city}, {address.state_or_province} {address.zip_code}</p>
                                                        <p className="text-sm">{address.country}</p>
                                                        <button
                                                            className={`mt-2 text-sm font-medium ${address.is_default ? 'text-gray-500' : 'text-blue-500 hover:underline'}`}
                                                            onClick={(e) => {
                                                                e.stopPropagation(); // Prevent triggering the parent click event
                                                                if (!address.is_default) {
                                                                    handleToggleDefaultAddress(address.id);
                                                                }
                                                            }}
                                                            disabled={address.is_default}
                                                        >
                                                            {address.is_default ? 'Default Address' : 'Set as Default'}
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        <button
                                            className="text-[#0B0C52] text-sm font-medium mb-4 hover:underline"
                                            onClick={() => setShowAddressForm(true)}
                                        >
                                            + Add new shipping address
                                        </button>
                                    </>
                                ) : (
                                    <form onSubmit={handleNewAddressSubmit} className="mb-4">
                                        {/* Address Form */}
                                        <div className="grid grid-cols-2 gap-2 mb-2 text-sm">
                                            <input
                                                type="text"
                                                name="firstName"
                                                placeholder="First Name"
                                                className="border border-gray-300 p-2"
                                                value={newAddress.firstName}
                                                onChange={(e) => setNewAddress({ ...newAddress, firstName: e.target.value })}
                                                required
                                            />
                                            <input
                                                type="text"
                                                name="lastName"
                                                placeholder="Last Name"
                                                className="border border-gray-300 p-2"
                                                value={newAddress.lastName}
                                                onChange={(e) => setNewAddress({ ...newAddress, lastName: e.target.value })}
                                                required
                                            />
                                            <input
                                                type="text"
                                                name="street"
                                                placeholder="Street"
                                                className="border border-gray-300 p-2 col-span-2"
                                                value={newAddress.street}
                                                onChange={(e) => setNewAddress({ ...newAddress, street: e.target.value })}
                                                required
                                            />

                                            <input
                                                type="text"
                                                name="address_line_1"
                                                placeholder="Address Line 1"
                                                className="border border-gray-300 p-2 col-span-2"
                                                value={newAddress.address_line_1}
                                                onChange={(e) => setNewAddress({ ...newAddress, address_line_1: e.target.value })}
                                            />
                                            <input
                                                type="text"
                                                name="address_line_2"
                                                placeholder="Address Line 2"
                                                className="border border-gray-300 p-2 col-span-2"
                                                value={newAddress.address_line_2}
                                                onChange={(e) => setNewAddress({ ...newAddress, address_line_2: e.target.value })}
                                            />

                                            <input
                                                type="text"
                                                name="city"
                                                placeholder="City"
                                                className="border border-gray-300 p-2"
                                                value={newAddress.city}
                                                onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })}
                                                required
                                            />
                                            <input
                                                type="text"
                                                name="state"
                                                placeholder="State/Province"
                                                className="border border-gray-300 p-2"
                                                value={newAddress.state}
                                                onChange={(e) => setNewAddress({ ...newAddress, state: e.target.value })}
                                                required
                                            />
                                            <input
                                                type="text"
                                                name="country"
                                                placeholder="Country"
                                                className="border border-gray-300 p-2"
                                                value={newAddress.country}
                                                onChange={(e) => setNewAddress({ ...newAddress, country: e.target.value })}
                                                required
                                            />
                                            <input
                                                type="text"
                                                name="zipCode"
                                                placeholder="Zip Code"
                                                className="border border-gray-300 p-2"
                                                value={newAddress.zipCode}
                                                onChange={(e) => setNewAddress({ ...newAddress, zipCode: e.target.value })}
                                                required
                                            />
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                type="submit"
                                                className="flex-1 bg-[#0B0C52] text-white py-2 rounded-md hover:bg-[#002080]"
                                            >
                                                Save Address
                                            </button>
                                            <button
                                                type="button"
                                                className="flex-1 bg-gray-300 text-gray-800 py-2 rounded-md hover:bg-gray-400"
                                                onClick={() => setShowAddressForm(false)}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </form>
                                )}

                                <div className="flex items-start gap-2 mb-4 text-sm">
                                    <input type="checkbox" id="agree" className="mt-1 accent-[#0B0C52]" checked={isTermsChecked}
                                        onChange={(e) => setIsTermsChecked(e.target.checked)} />
                                    <label htmlFor="agree">
                                        I Agree To The{' '}
                                        <Link to="/terms" className="text-blue-500 underline">
                                            Terms & Conditions
                                        </Link>{' '}
                                        And{' '}
                                        <Link to="/privacy" className="text-blue-500 underline">
                                            Privacy Policy
                                        </Link>
                                    </label>
                                </div>
                                {paymentError && (
                                    <div className="mb-4 text-sm text-red-600 bg-red-100 p-2 rounded">
                                        {paymentError}
                                    </div>
                                )}

                                <button
                                    className={`bg-[#FFD100] hover:bg-[#FFC300] text-black font-semibold w-full py-2 rounded-md ${isCardDetailsValid && isTermsChecked ? '' : 'opacity-50 cursor-not-allowed'
                                        }`}
                                    onClick={handlePaymentSubmit}
                                    disabled={!isCardDetailsValid || !isTermsChecked || orderSubmissionStatus === 'loading'}
                                >
                                    {orderSubmissionStatus === 'loading' ? (
                                        <>
                                            <svg
                                                className="animate-spin -ml-1 mr-3 h-5 w-5 text-black inline"
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                            >
                                                <circle
                                                    className="opacity-25"
                                                    cx="12"
                                                    cy="12"
                                                    r="10"
                                                    stroke="currentColor"
                                                    strokeWidth="4"
                                                ></circle>
                                                <path
                                                    className="opacity-75"
                                                    fill="currentColor"
                                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                ></path>
                                            </svg>
                                            Processing...
                                        </>
                                    ) : (
                                        'Pay Now'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <CheckoutOrderCouponsWidget
                orderTotal={priceCalculator?.total_price || 0}
                onCouponApply={handleCouponApply}
            />
          <CheckoutExpiringAlert onCouponApply={handleCouponApply} />

        </div>
    );

}